import "./lib/supabase" // has to be at the top to bootstrap correct supabase client
import { StrictMode } from "react"
import { createRoot } from "react-dom/client"
import "./style.css"

import { RouterProvider, createRouter, Navigate } from "@tanstack/react-router"
import { routeTree } from "./routeTree.gen"
import { ThemeProvider } from "./contexts/theme"
import { AuthProvider } from "./contexts/auth"
import { FieldbookPersistQueryClientProvider } from "./lib/queryClient"
import { useAuth } from "./hooks/useAuth"
import { getActiveProject } from "./hooks/useActiveProject"
import { SwStatusProvider } from "@/contexts/swStatus"
import { PowerSyncProvider } from "./contexts/PowerSync"
import { SyncStatusProvider } from "@/contexts/SyncStatus"
import * as Sentry from "@sentry/react"
import { ErrorFallback } from "./components/app/ErrorFallback"
import { AppInstallPrompt, appShell } from "@/platform"
import { installHttpCache } from "@/lib/offline/httpCache"
import { initStorageHealth } from "@/lib/storageHealth"

if (__FIELDBOOK_TARGET__ === "capacitor") {
  installHttpCache()
}

// Create a new router instance
const router = createRouter({
  routeTree,
  defaultPreload: "intent",
  notFoundMode: "root",
  defaultNotFoundComponent: () => {
    const activeProjectId = getActiveProject()
    if (activeProjectId) {
      return (
        <Navigate to="/projects/$id" params={{ id: activeProjectId }} replace />
      )
    }
    return <Navigate to="/projects" replace />
  },
  defaultOnCatch(error, errorInfo) {
    Sentry.captureReactException(error, errorInfo)
  },
  context: {
    isLoggedIn: false,
  },
})

Sentry.init({
  dsn: import.meta.env.VITE_SENTRY_DSN,
  initialScope: {
    tags: {
      app_version: __BUILD_ID__,
      app_target: __FIELDBOOK_TARGET__,
      app_platform: appShell.platform(),
    },
  },
  sendDefaultPii: true,
  integrations: [Sentry.tanstackRouterBrowserTracingIntegration(router)],
  transport: Sentry.makeBrowserOfflineTransport(Sentry.makeFetchTransport),
  ignoreErrors: [
    // mapbox-gl's MapLoadEvent calls a nulled errorCb when its telemetry POST
    // fails after map.remove() (typically events.mapbox.com being blocked)
    "this.errorCb is not a function",
  ],
  beforeSend(event) {
    // Drop network errors that occur while the device is offline.
    // These are expected — the SW cache may not have the data yet,
    // and the query will retry automatically when back online.
    if (!navigator.onLine) {
      const message = event.exception?.values?.[0]?.value ?? event.message ?? ""
      if (
        message.includes("Load failed") ||
        message.includes("Failed to fetch") ||
        message.includes("NetworkError")
      ) {
        return null
      }
    }
    return event
  },
})

void initStorageHealth()

const initialAppSettings = localStorage.getItem("fieldbook.appSettings")
let initialAppTheme: string | null = null
try {
  initialAppTheme = initialAppSettings
    ? ((JSON.parse(initialAppSettings) as { theme?: string }).theme ?? null)
    : null
} catch {
  initialAppTheme = null
}

if (initialAppTheme === "dark" || initialAppTheme === "light") {
  document.documentElement.classList.add(initialAppTheme)
} else if (window.matchMedia("(prefers-color-scheme: dark)").matches) {
  document.documentElement.classList.add("dark")
} else {
  document.documentElement.classList.add("light")
}

// Register the router instance for type safety
declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router
  }
}

const AppInner = () => {
  // ensures updated auth state is passed to router context
  const { isLoggedIn } = useAuth()
  return <RouterProvider router={router} context={{ isLoggedIn }} />
}

export const App = () => {
  return (
    <ThemeProvider>
      <AuthProvider>
        <SwStatusProvider>
          <PowerSyncProvider>
            <SyncStatusProvider>
              <AppInner />
            </SyncStatusProvider>
          </PowerSyncProvider>
        </SwStatusProvider>
      </AuthProvider>
    </ThemeProvider>
  )
}
appShell.prepareDocument()

const rootElement = document.getElementById("root")
if (!rootElement) throw new Error("No root element found")
if (!rootElement.innerHTML) {
  const root = createRoot(rootElement)
  root.render(
    <StrictMode>
      {/* Once inside react-router, this Error boundary doesn't do anything because it has its own error boundary */}
      <Sentry.ErrorBoundary
        fallback={({ error, resetError, eventId }) => (
          <ErrorFallback
            error={error}
            resetError={resetError}
            eventId={eventId}
          />
        )}
      >
        <FieldbookPersistQueryClientProvider>
          <App />
        </FieldbookPersistQueryClientProvider>
        <AppInstallPrompt />
      </Sentry.ErrorBoundary>
    </StrictMode>,
  )
}
